import React, { useEffect, useCallback } from "react";
import { Box, Chip } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTablePagination } from "hooks";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import { useLazyListPriceChangeLogsQuery } from "store/services/shipmentApi";
import HighlightedText from "components/Shared/HighlightedText";

const getChangeTypeStyle = (changeType) => {
  const styles = {
    create: { backgroundColor: "#EBF5FB", textColor: "green" },
    update: { backgroundColor: "#E9ECEF", textColor: "#6C757D" },
    delete: { backgroundColor: "#FADBD8", textColor: "#C0392B" },
  };
  return styles[changeType] || { backgroundColor: "#E9ECEF", textColor: "#6C757D" };
};

const PriceChangeLogList = ({ priceId }) => {
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();
  const [searchParams] = useSearchParams();
  const keyword = searchParams.get("search") || "";
  const [fetchError, setFetchError] = React.useState(null);

  const [fetchLogs, { data: logRes, isLoading, isFetching, error: apiError }] = useLazyListPriceChangeLogsQuery();

  const fetchPriceChangeLogs = useCallback(() => {
    if (!priceId) {
      setFetchError("Price ID is missing");
      toast.error("Price ID is missing");
      return;
    }
    setFetchError(null);
    fetchLogs({
      priceId,
      params: {
        page: currentPage,
        limit: perPage,
        ...(keyword && { keyword }),
      },
    }).catch((err) => {
      setFetchError(`Failed to fetch price change logs: ${err.message}`);
      toast.error(`Failed to fetch price change logs: ${err.message}`);
    });
  }, [fetchLogs, currentPage, perPage, keyword, priceId]);

  useEffect(() => {
    if (priceId) {
      fetchPriceChangeLogs();
    }
  }, [fetchPriceChangeLogs, priceId]);

  useEffect(() => {
    if (apiError) {
      const errorMessage = apiError?.data?.message || apiError.message || "Unknown error";
      setFetchError(`API Error: ${errorMessage}`);
      toast.error(`API Error: ${errorMessage}`);
    }
  }, [apiError]);

const columns = [
  {
    name: "Description",
    selector: (row) => row.description,
    cell: (row) => {
      const parts = (row.description || "N/A").split(";").filter(Boolean);
      return (
        <div style={{ lineHeight: "1.5", minWidth: 200 }}>
          {parts.map((part, idx) => (
            <div key={idx}>
              <HighlightedText text={part.trim()} highlight={keyword} />
            </div>
          ))}
        </div>
      );
    },
    minWidth: "300px",
  },
{
  name: "Old Value",
  selector: (row) => row.oldValue,
  cell: (row) => {
    if (row.field === "multiple" && Array.isArray(row.oldValue)) {
      return (
        <div style={{ lineHeight: "1.6" }}>
          {row.oldValue.map((item, idx) => {
            const text = Object.entries(item)
              .map(([key, value]) => `${key}: ${value?.toString() || "N/A"}`)
              .join(", ");
            return <div key={idx}>{text}</div>;
          })}
        </div>
      );
    }
    return row.oldValue?.toString() || "N/A";
  },
  minWidth: "300px",
},
{
  name: "New Value",
  selector: (row) => row.newValue,
  cell: (row) => {
    if (row.field === "multiple" && Array.isArray(row.newValue)) {
      return (
        <div style={{ lineHeight: "1.6" }}>
          {row.newValue.map((item, idx) => {
            const text = Object.entries(item)
              .map(([key, value]) => `${key}: ${value?.toString() || "N/A"}`)
              .join(", ");
            return <div key={idx}>{text}</div>;
          })}
        </div>
      );
    }
    return row.newValue?.toString() || "N/A";
  },
  minWidth: "300px",
},

  {
    name: "Changed By",
    selector: (row) => row.changedBy?.name,
    cell: (row) => (
      <HighlightedText text={row.changedBy?.name || "N/A"} highlight={keyword} />
    ),
    minWidth: "150px",
  },
  {
    name: "Change Type",
    selector: (row) => row.changeType,
    cell: (row) => (
      <Chip
        label={row.changeType?.toUpperCase() || "N/A"}
        variant="outlined"
        sx={{
          backgroundColor: getChangeTypeStyle(row.changeType).backgroundColor,
          color: getChangeTypeStyle(row.changeType).textColor,
          fontWeight: "bold",
          height: "17px",
          borderRadius: "12px",
          width: "auto",
          padding: "0",
          border: "none",
          "& .MuiChip-label": {
            fontSize: "0.6rem",
            padding: "3px 5px",
            lineHeight: "14px",
          },
        }}
      />
    ),
    minWidth: "120px",
    center: true,
  },
  {
    name: "Updated At",
    selector: (row) => row.updatedAt,
    cell: (row) => <AppTableDateTime date={row.updatedAt} />,
    minWidth: "170px",
  },
];


  return (
    <Box sx={{ p: 3 }}>
      {fetchError && (
        <Box sx={{ mb: 2, color: "error.main" }}>
          {fetchError}
        </Box>
      )}
      <AppTable
        columns={columns}
        data={logRes?.data ?? []}
        progressPending={isLoading || isFetching}
        paginationServer
        paginationPerPage={perPage}
        paginationTotalRows={logRes?.totalItems ?? 0}
        paginationDefaultPage={currentPage}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handlePerRowsChange}
      />
    </Box>
  );
};

export default PriceChangeLogList;